import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Tab, Tabs, Alert,
  InputAdornment, IconButton, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, MenuBook } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

export default function WelcomeScreen() {
  const { login, signup } = useAuth();
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 0) {
        await login(email, password);
      } else {
        await signup(email, password);
      }
    } catch (err) {
      setError(err.message.replace('Firebase: ', '').replace(/\(auth\/.*\)\.?/, '').trim());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(145deg, #0c0b09 0%, #1a160f 50%, #0c0b09 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {/* Decorative background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {['🍳', '🌿', '🍋', '🥘', '🫒', '🧄'].map((emoji, i) => (
          <Typography
            key={i}
            sx={{
              position: 'absolute',
              fontSize: { xs: '3rem', md: '5rem' },
              opacity: 0.04,
              top: `${[10, 70, 30, 80, 50, 20][i]}%`,
              left: `${[5, 85, 45, 15, 70, 90][i]}%`,
              transform: `rotate(${[15, -20, 10, -15, 25, -10][i]}deg)`,
              userSelect: 'none',
            }}
          >
            {emoji}
          </Typography>
        ))}
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 440 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #f0873c, #c96820)',
              mb: 2,
              boxShadow: '0 8px 32px rgba(240,135,60,0.4)',
            }}
          >
            <MenuBook sx={{ fontSize: 36, color: '#fff' }} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              background: 'linear-gradient(135deg, #f5a76c, #f0873c)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5,
            }}
          >
            RecipeBook
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your personal recipe collection
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setError(''); }}
            variant="fullWidth"
            sx={{
              mb: 3,
              '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
              '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Create Account" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Password"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoComplete={tab === 0 ? 'current-password' : 'new-password'}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(!showPass)} edge="end">
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{
                mt: 1,
                py: 1.5,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #f0873c, #c96820)',
                '&:hover': { background: 'linear-gradient(135deg, #f5a76c, #f0873c)' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : tab === 0 ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>
          </Box>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          Your recipes, beautifully organized.
        </Typography>
      </Box>
    </Box>
  );
}
